import appError from '#shared/util/appError.js';
import validate from '#shared/util/validate.js';

/**
 * Field validation method
 * @param {object} element Element to be validated
 * @param {string} type Validation type
 * @param {string} [ariaDescribedBy] ID of the element containing the error message
 * @param {object} [group] Object with data for grouped inputs
 * @param {Array} [group.errors] Input group errors
 * @param {string} [group.labelFor] ID of the input with the error within the group
 * @param {string} [group.errorMessage] Error message to be displayed for the group
 * @param {Function} [setAdaptiveError] Pass object for adaptive error messages
 * @param {boolean} [isOptional] The input field could be empty without validation
 * @param {string} [validationPatterns] Custom validation regular expresion
 * @returns {Array|boolean} If group.errors was defined, returns a new Array of errors. Otherwise returns true or false.
 */
const validateField = (element, type, ariaDescribedBy, group = null, setAdaptiveError = () => {}, isOptional = false, validationPatterns) => {
	let formLine = '';
	const errors = group && group.errors && Array.isArray(group.errors) ? [...group.errors] : null;
	const closest = (element, fn) => element && (fn(element) ? element : closest(element.parentNode, fn));
	if (type) {
		// If the type is 'tax', provide validation patterns (string of regEx validation patterns)
		const validationResult = type === 'tax' && validationPatterns ? validate[type](element.value, validationPatterns) : validate[type](element.value);
		// Remove errors if it's valid or optional (+ not filled)
		if (validationResult.isValid || isOptional && !element.value) {
			setAdaptiveError(null);
			if (element.classList.length) {
				element.removeAttribute('aria-describedby');
				element.removeAttribute('aria-invalid');
				formLine = closest(element, (el) => el.classList.contains('formLine'));
				if (formLine) {
					formLine.__vue__.hasError = false;
				}
			}
			if (errors) {
				return errors.filter((error) => error.errorMessage !== group.errorMessage);
			} else {
				return true;
			}
		} else {
			setAdaptiveError(validationResult.data);
			element.setAttribute('aria-describedby', ariaDescribedBy);
			element.setAttribute('aria-invalid', true);
			formLine = closest(element, (el) => el.classList.contains('formLine'));
			if (formLine) {
				formLine.__vue__.hasError = true;
			}
			if (errors && !errors.find((error) => error.errorMessage === group.errorMessage)) {
				return [...errors, {
					labelFor: group.labelFor,
					errorMessage: group.errorMessage
				}];
			} else if (errors) {
				return errors;
			} else {
				return false;
			}
		}
	} else {
		throw appError('Missing type argument in validateField', {
			code: 'APP_FRONTEND_UTIL',
			origin: 'validateField'
		});
	}
};

export default validateField;
